import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import { useAppContext } from '../contexts/AppContext';
import { Shield, Search, TestTube2 } from 'lucide-react';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { useProjectFilter } from '../hooks/useProjectFilter';

const TraceabilityMatrix: React.FC = () => {
    const { allData, activeProject } = useAppContext();
    const navigate = useNavigate();
    const { fmeaItems, dvprTests, pDiagramList } = allData;
    const { isItemInProject } = useProjectFilter();

    // Filtered Data Sources
    const filteredPDiagrams = useMemo(() => {
        return (pDiagramList || []).filter(p => isItemInProject(p.projectName, p.epochId) && (p.status || 'active') === 'active');
    }, [pDiagramList, activeProject, allData.iterationHistory]);

    const filteredFmeaItems = useMemo(() => {
        return (fmeaItems || []).filter(item => isItemInProject(item.projectName, item.epochId));
    }, [fmeaItems, activeProject, allData.iterationHistory]);

    const filteredRequirements = useMemo(() => {
        return (allData.customerRequirements || []).filter(req => isItemInProject(req.projectName, req.epochId));
    }, [allData.customerRequirements, activeProject]);

    const filteredTests = useMemo(() => {
        return (dvprTests || []).filter(t => isItemInProject(t.projectName, t.epochId));
    }, [dvprTests, activeProject, allData.iterationHistory]);

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">System Traceability Matrix</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-text-secondary">End-to-end traceability from Customer Requirements to Product Features, Risks, and Verification Tests.</p>
                        <ActiveProjectBadge showStandard={false} />
                    </div>
                </div>
                <ModuleInfoModal
                    title="System Traceability Matrix"
                    description="The 'Single Source of Truth' for project integrity. This matrix connects every customer promise to a technical feature, a risk assessment, and a physical test."
                    benefits={[
                        "Requirement Coverage: Ensure 100% of customer needs are addressed by features.",
                        "Safety Validation: Verify that every safety requirement has a corresponding risk analysis.",
                        "Test Verification: Ensure all features and risks are covered by the DVP&R test plan."
                    ]}
                    usageInstructions={[
                        "Check the 'Requirement' column for gaps—if a requirement has no 'Risks', it is an unanalyzed hazard.",
                        "Verify 'Test Plan' coverage—blank cells indicate missing validation steps.",
                        "Click any ID to jump directly to that module for editing."
                    ]}
                />
            </div>

            {/* Workflow Visualization */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                    { step: 1, name: 'Spec / Req', status: filteredRequirements.length > 0 ? 'complete' : 'pending' },
                    { step: 2, name: 'P-Diagram / Func', status: filteredPDiagrams.length > 0 ? 'complete' : 'pending' },
                    { step: 3, name: 'FMEA / Risk', status: filteredFmeaItems.length > 0 ? 'complete' : 'pending' },
                    { step: 4, name: 'DVP&R / Test', status: filteredTests.length > 0 ? 'complete' : 'pending' },
                ].map((s) => (
                    <div key={s.step} className={`p-3 rounded-lg border flex flex-col items-center gap-1 ${s.status === 'complete' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-100 border-slate-300 dark:bg-slate-800 dark:border-slate-700'}`}>
                        <span className={`text-[10px] font-bold uppercase ${s.status === 'complete' ? 'text-emerald-500' : 'text-slate-500'}`}>Step {s.step}</span>
                        <span className="text-xs font-bold">{s.name}</span>
                        <div className={`w-2 h-2 rounded-full ${s.status === 'complete' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-300'}`} />
                    </div>
                ))}
            </div>

            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-text-secondary border-collapse">
                        <thead className="text-[11px] text-text-primary uppercase bg-slate-50 dark:bg-slate-800/50 sticky top-0 z-10">
                            <tr>
                                <th scope="col" className="px-4 py-3 border-b border-border w-1/4">1. Customer Requirement</th>
                                <th scope="col" className="px-4 py-3 border-b border-border w-1/5">2. Product Feature / Function</th>
                                <th scope="col" className="px-4 py-3 border-b border-border w-1/4">3. FMEA Risks & Hazards</th>
                                <th scope="col" className="px-4 py-3 border-b border-border w-1/4">4. DVP&R Test Plan</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {filteredRequirements.map(req => {
                                // 1. Map Requirement to Features
                                const relatedFeatures = (allData.productFeatures || []).filter(pf =>
                                    (pf.linkedCustomerReqIds || []).includes(req.id)
                                );

                                // 2. Map Features/Requirement to FMEAs
                                const relatedFmeas = filteredFmeaItems.filter(f =>
                                    (f.linkedCustomerReqIds || []).includes(req.id) ||
                                    relatedFeatures.some(pf => (f.linkedProductFeatureIds || []).includes(pf.id))
                                );

                                // 3. Map FMEAs/Requirement to Tests
                                const relatedFmeaIds = relatedFmeas.map(f => f.id);
                                const relatedTests = filteredTests.filter(t =>
                                    (t.linkedCustomerReqIds || []).includes(req.id) ||
                                    (t.traceableRiskId || []).some(rId => relatedFmeaIds.includes(rId))
                                );

                                return (
                                    <tr key={req.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                                        <td className="px-4 py-4 border-r border-border/50 align-top">
                                            <div className="flex flex-col gap-1">
                                                <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded w-fit">{req.id}</span>
                                                <p className="text-xs font-semibold text-text-primary leading-relaxed">{req.requirementText}</p>
                                                {req.metric && (
                                                    <span className="text-[10px] text-text-secondary italic">Spec: {req.metric} {req.target}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 border-r border-border/50 align-top bg-slate-50/10">
                                            {relatedFeatures.length > 0 ? (
                                                <div className="flex flex-col gap-3">
                                                    {relatedFeatures.map(pf => (
                                                        <div key={pf.id} className="flex flex-col gap-0.5">
                                                            <span className="text-[9px] font-bold text-purple-500 uppercase">{pf.id}</span>
                                                            <span className="text-xs font-medium text-text-primary">{pf.name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center h-full py-4 opacity-40">
                                                    <Search size={14} className="text-text-secondary mb-1" />
                                                    <span className="text-[10px] italic">No technical link</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 border-r border-border/50 align-top">
                                            {relatedFmeas.length > 0 ? (
                                                <ul className="space-y-3">
                                                    {relatedFmeas.map(fmea => (
                                                        <li key={fmea.id} className="flex flex-col gap-1 group">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-[9px] font-bold text-red-500">{fmea.id}</span>
                                                                <span className={`text-[9px] px-1 rounded font-bold ${fmea.S >= 8 ? 'bg-red-500 text-white' : 'bg-amber-100 text-amber-700'}`}>S:{fmea.S}</span>
                                                            </div>
                                                            <button onClick={() => navigate('/fmeas', { state: { id: fmea.id } })} className="text-left hover:underline text-xs text-text-primary leading-tight">
                                                                {fmea.failureMode}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center h-full py-4 bg-red-50 dark:bg-red-950/20 rounded border border-dashed border-red-200 dark:border-red-800">
                                                    <Shield size={14} className="text-red-400 mb-1" />
                                                    <span className="text-[10px] text-red-400 font-semibold uppercase">Risk Analysis Missing</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 align-top">
                                            {relatedTests.length > 0 ? (
                                                <ul className="space-y-3">
                                                    {relatedTests.map(test => (
                                                        <li key={test.testId} className="flex flex-col gap-1">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-[9px] font-bold text-blue-500 italic">{test.testId}</span>
                                                                <span className={`text-[9px] px-1 rounded font-bold uppercase ${test.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                                                    {test.status || 'Planned'}
                                                                </span>
                                                            </div>
                                                            <button onClick={() => navigate('/dvpr', { state: { id: test.testId } })} className="text-left hover:underline text-xs text-text-primary leading-tight">
                                                                {test.testName}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                <div className="flex flex-col items-center justify-center h-full py-4 bg-blue-50 dark:bg-blue-950/20 rounded border border-dashed border-blue-200 dark:border-blue-800">
                                                    <TestTube2 size={14} className="text-blue-400 mb-1" />
                                                    <span className="text-[10px] text-blue-400 font-semibold uppercase">Validation Missing</span>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredRequirements.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <Search size={32} className="text-text-secondary opacity-20" />
                                            <p className="text-text-secondary">No requirements found for the active project.</p>
                                            <p className="text-xs text-text-secondary italic">Define customer requirements in the &quot;Customer Requirements&quot; module to see traceability.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};

export default TraceabilityMatrix;
